import TagUserApi from '@/api/TagUserApi';
import { i18n } from '@/i18n';
import router from '@/router/index.router';
import { useModal, useNotif } from '@orion.ui/orion';
import { defineAsyncComponent } from 'vue';
import useColorService from '../ColorService';
import useSelfService from '../SelfService';

class TagUserService {

	private get api () { return TagUserApi }
	private get self () { return useSelfService().self }

	async addAsync (tagId: number, userIds: number[], organizationId = this.self.organization?.id) {
		if (!organizationId) return;

		try {
			const { data } = await this.api.add({
				tagId,
				userIds,
				organizationId,
			});
			return data;
		}
		catch (e: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
		}
	}

	async deleteAsync (ids: number[]) {
		try {
			const { data } = await this.api.delete({ ids });
			return data;
		}
		catch (e: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
		}
	}

	async userListAsync (tagId: number, withHiddenUsers = false) {
		try {
			const { data } = await TagUserApi.listUser(tagId, withHiddenUsers);
			return data;
		}
		catch (e: any) {
			useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
		}
	}

	async promptUsersToAddTagAsync (tagId: number, showHiddenUser: boolean, organizationId = this.self.organization?.id) {
		if (!organizationId) {
			router.push({ name: 'UserGroupDashboard' });
			return;
		}

		return new Promise<Undef<IOrganizationUserListItemModel[]>>(async (resolve) => {
			useModal({
				Nested: defineAsyncComponent(() => import('@/components/shared/SharedSelectUserList.vue')),
				NestedProps: {
					title: i18n.global.t('user.selectionnezdesusers'),
					excludeTagId: tagId,
					showHiddenUser,
				},
				size: 'sm',
				customClass: 'user-select-modal-wrapper',
				events: {
					'cancel': () => resolve(undefined),
					'add': async (Modal, payload: IOrganizationUserListItemModel[]) => {
						Modal._loader()?.show(i18n.global.t('user.enregistrementDesTags'));

						try {
							const userIds = payload.map(x => x.user?.id) as number[];
							await this.api.add({
								userIds,
								tagId,
								organizationId,
							});
						}
						catch (e: any) {
							useNotif.danger(i18n.global.t('common.notification.oops'), e?.response?.data);
						}

						Modal._loader()?.hide();
						resolve(payload);
						Modal.close();
					},
				},
			});
		});
	}

	async selfAsync (...args: Parameters<typeof TagUserApi.self>) {
		const { data } = await TagUserApi.self(...args);
		return data.map((x) => {
			return {
				...x,
				tagColorName: useColorService().swapColor(x.tagColorName),
			};
		});
	}

}

const tagUserServiceSingleton = new TagUserService();

export default function useTagUserService () {
	return tagUserServiceSingleton;
}
